import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { topics } from '@/mocks/topics';
import { articleBodies } from '@/mocks/articleBodies';

export default function ArticlePage() {
  const { id } = useParams();
  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const topic = useMemo(() => (article ? topics.find((t) => t.id === article.topicId) : undefined), [article]);

  useEffect(() => {
    document.title = article
      ? `${article.title} - 资讯阅读 - 姐姐的朋友影视内容站`
      : '资讯不存在 - 姐姐的朋友影视内容站';
  }, [article]);

  const related = useMemo(
    () => (article ? articles.filter((a) => a.topicId === article.topicId && a.id !== article.id) : []),
    [article],
  );

  if (!article) {
    return (
      <div className="pt-16 md:pt-[72px]">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">资讯不存在或已删除</h1>
          <p className="text-sm text-foreground-500 mb-6">该篇资讯可能已被移除</p>
          <Link
            to="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const body = articleBodies[article.id] ?? [article.summary];

  return (
    <div className="pt-16 md:pt-[72px]">
      <article className="max-w-[820px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '资讯专题', to: topic ? `/topic/${topic.id}` : '/' },
            { label: article.title },
          ]}
        />

        <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-tight mt-5 mb-4">
          {article.title}
        </h1>
        <div className="flex items-center flex-wrap gap-3 text-xs text-foreground-400 mb-7">
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {article.date}
          </span>
          {topic && (
            <Link
              to={`/topic/${topic.id}`}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
            >
              <i className="ri-price-tag-3-line" />
              {topic.title}
            </Link>
          )}
        </div>

        <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-8">
          <img
            src={article.cover}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="flex flex-col gap-5">
          {body.map((paragraph, index) => (
            <p key={index} className="text-base md:text-lg text-foreground-700 leading-loose">
              {paragraph}
            </p>
          ))}
        </div>

        <p className="text-xs text-foreground-400 mt-10 pt-6 border-t border-background-200">
          本文内容为演示数据，仅用于展示网站的资讯阅读体验。
        </p>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 flex items-center gap-3 mb-5">
              <span className="w-1.5 h-5 rounded-full bg-accent-500" />
              相关阅读
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group flex items-center justify-between gap-4 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="font-medium text-sm md:text-base text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                    {item.title}
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}